import React from 'react';
import {
  FiTrendingUp,
  FiTrendingDown,
  FiEye,
  FiUsers,
  FiTarget,
  FiCheckCircle,
  FiDollarSign,
  FiInfo,
} from 'react-icons/fi';
import type { KpiValue, MarketingDashboardKpis } from '@troia-v3/types';

interface MarketingKpiHeroRowProps {
  kpis: MarketingDashboardKpis;
  /** Badge do card de visitantes. `null` = número medido e completo, sem badge. */
  visitorsBadge?: KpiBadge | null;
}

/** Aviso curto ao lado do rótulo do KPI. */
export interface KpiBadge {
  label: string;
  title: string;
}

const formatBRL = (v: number): string =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(v);

const formatNumber = (v: number): string =>
  new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(v);

const formatDeltaPct = (v: number): string => {
  const abs = Math.abs(v);
  return `${new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 }).format(abs)}%`;
};

interface KpiCardProps {
  label: string;
  value: string;
  kpi: KpiValue;
  icon: React.ReactNode;
  testId: string;
  badge?: KpiBadge | null;
  /** Sufixo do data-testid do badge — permite o teste distinguir os estados. */
  badgeTestId?: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value, kpi, icon, testId, badge, badgeTestId }) => {
  const positive = kpi.trend === 'up';
  const negative = kpi.trend === 'down';
  return (
    <div
      className="rounded-xl p-3.5"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        border: '1px solid var(--theme-card-border)',
      }}
      data-testid={testId}
    >
      {/* Topo: caixa de ícone (esq) + delta pill (dir) */}
      <div className="flex items-center justify-between gap-2">
        <span
          className="flex items-center justify-center flex-shrink-0"
          style={{
            width: 30,
            height: 30,
            borderRadius: 8,
            backgroundColor: 'var(--theme-page-background)',
            border: '1px solid var(--theme-card-border)',
            color: 'var(--theme-card-text)',
          }}
        >
          {icon}
        </span>
        <span
          className="inline-flex items-center gap-0.5 text-[11px] font-semibold px-1.5 py-0.5 rounded-full"
          style={{
            backgroundColor: positive
              ? 'rgba(16, 185, 129, 0.12)'
              : negative
                ? 'rgba(239, 68, 68, 0.12)'
                : 'rgba(148, 163, 184, 0.14)',
            color: positive
              ? 'var(--theme-success-color, #10b981)'
              : negative
                ? 'var(--theme-error-color, #ef4444)'
                : 'var(--theme-muted-text)',
          }}
        >
          {positive ? <FiTrendingUp size={11} /> : negative ? <FiTrendingDown size={11} /> : null}
          {formatDeltaPct(kpi.deltaPct)}
        </span>
      </div>

      {/* Label (+ badge estimado) */}
      <div
        className="text-xs mt-3 mb-1 flex items-center gap-1.5"
        style={{ color: 'var(--theme-muted-text)' }}
      >
        {label}
        {badge && (
          <span
            className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
            // Tokens de tema, não hex: o badge é um AVISO, e precisa continuar
            // legível no tema escuro e no white-label de cada cliente.
            style={{
              backgroundColor: 'var(--theme-warning-subtle)',
              color: 'var(--theme-warning-fg)',
            }}
            title={badge.title}
            data-testid={`${testId}-${badgeTestId ?? 'badge'}`}
          >
            <FiInfo size={10} />
            {badge.label}
          </span>
        )}
      </div>

      {/* Valor */}
      <div
        className="text-2xl font-bold"
        style={{ color: 'var(--theme-card-text)', letterSpacing: '-0.02em', lineHeight: 1.1 }}
      >
        {value}
      </div>
    </div>
  );
};

export const MarketingKpiHeroRow: React.FC<MarketingKpiHeroRowProps> = ({
  kpis,
  visitorsBadge = null,
}) => (
  <div
    className="grid grid-cols-2 max-[420px]:grid-cols-1 min-[560px]:grid-cols-3 min-[1180px]:grid-cols-5 gap-3"
    data-testid="marketing-kpi-row"
  >
    <KpiCard
      label="Visitantes"
      value={formatNumber(kpis.visitors.value)}
      kpi={kpis.visitors}
      icon={<FiEye size={15} />}
      testId="marketing-kpi-visitors"
      badge={visitorsBadge}
      badgeTestId={visitorsBadge?.label === 'estimado' ? 'proxy-badge' : 'partial-badge'}
    />
    <KpiCard
      label="Leads"
      value={formatNumber(kpis.leads.value)}
      kpi={kpis.leads}
      icon={<FiUsers size={15} />}
      testId="marketing-kpi-leads"
    />
    <KpiCard
      label="Propostas"
      value={formatNumber(kpis.proposals.value)}
      kpi={kpis.proposals}
      icon={<FiTarget size={15} />}
      testId="marketing-kpi-proposals"
    />
    <KpiCard
      label="Ganhos"
      value={formatNumber(kpis.won.value)}
      kpi={kpis.won}
      icon={<FiCheckCircle size={15} />}
      testId="marketing-kpi-won"
    />
    <KpiCard
      label="Receita"
      value={formatBRL(kpis.revenue.value)}
      kpi={kpis.revenue}
      icon={<FiDollarSign size={15} />}
      testId="marketing-kpi-revenue"
    />
  </div>
);

MarketingKpiHeroRow.displayName = 'MarketingKpiHeroRow';
